import React, { useEffect, useRef, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import CommonDropdown from "../../../components/commonDropDown/CommonDropDown";
import MultiSelectDropDown from "../../../components/multiSelect/MultiSelect";
import { datasets, industry } from "../../../constants/constants";
import { api1 } from "../../../utils/axiosInstance";
import Papa from "papaparse";
import { updateStep, updateSkillsData, updateCareerData, clearValidation } from "../../../redux/profileFormSlice";
import ValidationError from "../../../components/ValidationError/ValidationError";
import useLightcastToken from "../../../utils/useLightcastToken";

const schema = yup.object({
  currentJob: yup.string().required("Current job is required"),
  industry: yup.string().required("Industry is required"),
});

const SkillsCompetencies = () => {
  const dispatch = useDispatch();
  const { step5: stepData, skillsData, careerData: reduxCareerData, isCareerDataLoaded, validationStep } = useSelector((state) => state.profileForm);
  const { lightCastToken } = useLightcastToken();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCareerData, setIsLoadingCareerData] = useState(!isCareerDataLoaded);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState(true);
  const [industryData, setIndustryData] = useState([]);
  const isUpdatingFromRedux = useRef(false);
  
  const careerData = reduxCareerData.length > 0 ? reduxCareerData : [];

  const { register, watch, setValue, trigger, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      currentJob: stepData?.currentJob || "",
      industry: stepData?.industry || "",
      // commonSkills: stepData?.commonSkills || [],
      // definingSkills: stepData?.definingSkills || [],
      // distinguishingSkills: stepData?.distinguishingSkills || [],
      // salaryBoostingSkills: stepData?.salaryBoostingSkills || [],
      // certifications: stepData?.certifications || [],
    },
    mode: "onChange",
  });

  const selectedJob = watch("currentJob");

  const getSkillsForJob = useCallback((occupationId) => skillsData?.[occupationId] || null, [skillsData]);

  const filterSkills = useCallback((userSkills, availableSkills) =>
    userSkills?.filter(skill => availableSkills?.some(s => s.name === skill)) || []
  , []);

  // const filterAllSkills = useCallback((userSkills, lightcastSkills) => ({
  //   commonSkills: filterSkills(userSkills?.commonSkills, lightcastSkills?.common),
  //   definingSkills: filterSkills(userSkills?.definingSkills, lightcastSkills?.defining),
  //   distinguishingSkills: filterSkills(userSkills?.distinguishingSkills, lightcastSkills?.distinguishing),
  //   salaryBoostingSkills: filterSkills(userSkills?.salaryBoostingSkills, lightcastSkills?.salaryBoosting),
  //   certifications: filterSkills(userSkills?.certifications, lightcastSkills?.certifications),
  // }), [filterSkills]);

  const updateFormValues = useCallback((data) => {
    Object.entries(data).forEach(([key, value]) =>
      setValue(key, value ?? (key.includes("Skills") || key === "certifications" ? [] : ""), { shouldValidate: false })
    );
  }, [setValue]);

  const fetchSkills = useCallback(async (occupationId) => {
    if (!occupationId || !lightCastToken) return null;

    setIsLoading(true);
    try {
      const { data } = await api1.post("/api/lightcast/get-occupation-benchmark-data", {
        jobId: occupationId, datasets, token: lightCastToken
      });

      const skills = {
        // common: data?.data?.commonSkills || [],
        // defining: data?.data?.definingSkills || [],
        // distinguishing: data?.data?.distinguishingSkills || [],
        // salaryBoosting: data?.data?.salaryBoostingSkills || [],
        // certifications: data?.data?.certifications || []
      };

      dispatch(updateSkillsData({ jobId: occupationId, skills }));
      return skills;
    } catch (err) {
      console.error("Error fetching skills:", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [lightCastToken, dispatch]);

  const getOrFetchSkills = useCallback(async (occupationId) => getSkillsForJob(occupationId) || await fetchSkills(occupationId), [getSkillsForJob, fetchSkills]);

  useEffect(() => {
    if (isCareerDataLoaded && reduxCareerData.length > 0) {
      setIsLoadingCareerData(false);
      return;
    }

    (async () => {
      try {
        setIsLoadingCareerData(true);
        const response = await fetch("/output.csv");
        const parsed = Papa.parse(await response.text(), { header: true }).data;
        const formattedData = parsed.filter(r => r.name?.trim()).map(r => ({
          id: r.id, name: r.name, value: r.name, label: r.name
        }));
        
        dispatch(updateCareerData(formattedData));
      } catch (err) {
        console.error("Error loading job titles:", err);
      } finally {
        setIsLoadingCareerData(false);
      }
    })();
  }, [isCareerDataLoaded, reduxCareerData.length, dispatch]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoadingIndustries(true);
        const response = await fetch("/unique_industries_ascending.csv");
        const parsed = Papa.parse(await response.text(), { header: true }).data;
        const formattedData = parsed.filter(r => r["Industry Title"]?.trim()).map(r => ({
          value: r["Industry Title"], 
          label: r["Industry Title"]
        }));
        
        setIndustryData(formattedData);
      } catch (err) {
        console.error("Error loading industries:", err);
      } finally {
        setIsLoadingIndustries(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (stepData && Object.keys(stepData).length) {
      isUpdatingFromRedux.current = true;
      updateFormValues(stepData);
      setTimeout(() => isUpdatingFromRedux.current = false, 100);
    }
  }, [stepData, updateFormValues]);

  // useEffect(() => {
  //   if (!stepData?.currentJob || !careerData.length) return;
  //   const matchedJob = careerData.find(c => c.name.toLowerCase() === stepData.currentJob.toLowerCase());
  //   if (!matchedJob) return;

  //   getOrFetchSkills(matchedJob.id).then((skills) => {
  //     if (!skills) return;
  //     const filtered = filterAllSkills(stepData, skills);
  //     const hasChanges = Object.keys(filtered).some(k => JSON.stringify(filtered[k]) !== JSON.stringify(stepData[k]));
  //     if (hasChanges) dispatch(updateStep({ step: "step5", data: { ...stepData, ...filtered, occupationId: matchedJob.id } }));
  //   });
  // }, [stepData?.currentJob, careerData, getOrFetchSkills, dispatch, filterAllSkills]);

  useEffect(() => {
    const sub = watch(value => !isUpdatingFromRedux.current && dispatch(updateStep({ step: "step5", data: value })));
    return () => sub.unsubscribe();
  }, [watch, dispatch]);

  useEffect(() => {
    if (validationStep === 5) {
      trigger();
      dispatch(clearValidation());
    }
  }, [validationStep, trigger, dispatch]);

  // const renderSkillsSection = (title, description, name) => {
  //   const occupationId = careerData.find(c => c.name.toLowerCase() === selectedJob?.toLowerCase())?.id;
  //   const skills = occupationId ? getSkillsForJob(occupationId)?.[name.replace("Skills", "").toLowerCase()] || [] : [];
  //   const options = skills.map(s => ({ value: s.name, label: s.name, description: s.description, count: s.count, percentage: s.percentage }));

  //   return (
  //     <div className="p-4 rounded-lg border border-gray-300">
  //       <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
  //       <p className="text-gray-500 mb-4 text-xs">{description}</p>
  //       <MultiSelectDropDown
  //         label={`Select ${title}`}
  //         name={name}
  //         register={register}
  //         errors={errors}
  //         options={options}
  //         value={watch(name) || []}
  //         onChange={(values) => setValue(name, values, { shouldValidate: true })}
  //       />
  //     </div>
  //   );
  // };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <header className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Skills & Competencies</h2>
        <p className="text-gray-500 text-xs sm:text-sm">
          Select your current job to see relevant skills and competencies. Skills shown below help identify areas for growth and development.
        </p>
      </header>

      <form className="space-y-4 sm:space-y-6">
        <ValidationError message={Object.keys(errors).length > 0 ? "Please fill in all required fields" : null} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {isLoadingCareerData ? (
            <div className="space-y-2">
              <label className="font-bold text-base text-gray-700">Current Job / Experience *</label>
              <div className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg bg-gray-50">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                <span className="text-gray-600 text-sm">Loading job options...</span>
              </div>
            </div>
          ) : (
            <CommonDropdown label="Current Job / Experience" name="currentJob" required register={register} errors={errors} options={careerData} search defaultValue={stepData?.currentJob || ""} />
          )}
          {isLoadingIndustries ? (
            <div className="space-y-2">
              <label className="font-bold text-base text-gray-700">Industry *</label>
              <div className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg bg-gray-50">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                <span className="text-gray-600 text-sm">Loading industry options...</span>
              </div>
            </div>
          ) : (
            <CommonDropdown label="Industry" name="industry" required register={register} errors={errors} options={industryData} search defaultValue={stepData?.industry || ""} />
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-3"></div>
              <p className="text-gray-600">Loading skills data for {selectedJob}...</p>
            </div>
          </div>
        ) : selectedJob && (
          <>
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {renderSkillsSection("Common Skills", `Most commonly required skills for ${selectedJob}.`, "commonSkills")}
              {renderSkillsSection("Defining Skills", `Essential skills for entry-level ${selectedJob} positions.`, "definingSkills")}
              {renderSkillsSection("Distinguishing Skills", `Skills that help you stand out in ${selectedJob}.`, "distinguishingSkills")}
              {renderSkillsSection("Salary Boosting Skills", `High-value skills to increase earnings in ${selectedJob}.`, "salaryBoostingSkills")}
            </div>
            {renderSkillsSection("Certifications", `Certifications to enhance your ${selectedJob} profile.`, "certifications")} */}
          </>
        )}
      </form>
    </div>
  );
};

export default SkillsCompetencies;
